import React,{useState} from 'react';
import {useParams,Link,useLocation,useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {posesData} from '../data/poses';

const {FiArrowLeft,FiCopy,FiCheck,FiImage,FiCamera,FiPlus,FiCheckSquare,FiSquare}=FiIcons;

// Color mapping for categories
const categoryBackgroundColors={
  business: '#0F0F0F',
  senior: '#1C1C1C', 
  couple: '#2E2A2B',
  creative: '#3B2F2F',
  lifestyle: '#4B3B3F',
  fitness: '#5C4B51',
  artistic: '#6D5C5F',
  branding: '#7E6D6F'
};

const getCategoryId=(subject)=> {
  const subjectMap={
    'Business': 'business',
    'Senior': 'senior',
    'Couple': 'couple',
    'Creative': 'creative',
    'Lifestyle': 'lifestyle',
    'Fitness': 'fitness',
    'Artistic': 'artistic',
    'Branding': 'branding'
  };
  return subjectMap[subject] || 'business';
};

function PoseDetail() {
  const {poseId}=useParams();
  const location=useLocation();
  const navigate=useNavigate();
  const pose=posesData.find(p=> p.id===poseId);
  const [copied,setCopied]=useState(false);
  const [selected,setSelected]=useState(false);
  
  const getPoseNumber=(poseId)=> {
    const parts=poseId.split('-');
    return parts[1] ? parseInt(parts[1]) : 1;
  };

  React.useEffect(()=> {
    // Check if this pose is already in the selection
    const storedSelections=JSON.parse(localStorage.getItem('selectedPoses') || '[]');
    setSelected(storedSelections.includes(poseId));
  },[poseId]);

  if (!pose) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-slate-800 mb-4">Pose not found</h2>
        <Link to="/" className="text-brand-primary hover:text-brand-primary-dark">
          Return to home
        </Link>
      </div>
    );
  }

  const handleCopyInstruction=()=> {
    navigator.clipboard.writeText(pose.instruction);
    setCopied(true);
    setTimeout(()=> setCopied(false),2000);
  };

  const toggleSelection=()=> {
    const storedSelections=JSON.parse(localStorage.getItem('selectedPoses') || '[]');
    let newSelections;
    
    if (selected) {
      // Remove from selection
      newSelections=storedSelections.filter(id=> id !==poseId);
      setSelected(false);
    } else {
      // Add to selection
      newSelections=[...storedSelections,poseId];
      setSelected(true);
    }
    
    localStorage.setItem('selectedPoses',JSON.stringify(newSelections));
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  const categoryId=getCategoryId(pose.subject);
  const backgroundColor=categoryBackgroundColors[categoryId];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <motion.div initial={{y: -20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.6}} className="mb-8" >
        <div className="flex items-center justify-between">
          <Link to={location.state?.fromCategory ? `/category/${categoryId}` : `/category/${categoryId}`} className="inline-flex items-center text-slate-600 hover:text-slate-800 transition-colors text-sm" >
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to {pose.subject} Poses
          </Link>
          <h1 className="text-lg font-bold text-slate-800">{pose.subject} Pose {getPoseNumber(pose.id)}</h1>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div initial={{y: 20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.6,delay: 0.1}} className="bg-white rounded-2xl shadow-xl overflow-hidden" >
        {/* Selection Button */}
        <motion.button
          whileHover={{scale: 1.05}}
          whileTap={{scale: 0.95}}
          onClick={toggleSelection}
          className={`absolute top-4 right-4 z-20 px-4 py-2 rounded-full shadow-lg transition-all text-sm ${
            selected 
              ? 'bg-brand-accent text-white' 
              : 'bg-white text-slate-700 border border-slate-200'
          }`}
        >
          <SafeIcon icon={selected ? FiCheckSquare : FiSquare} className="mr-2" />
          {selected ? 'Selected' : 'Add to Selection'}
        </motion.button>

        {/* Image Section */}
        <div className="aspect-video md:aspect-square relative" style={{backgroundColor}}>
          <img 
            src={pose.link} 
            alt={`${pose.subject} Pose ${getPoseNumber(pose.id)}`} 
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
            onError={(e)=> {
              e.target.style.display='none';
              e.target.nextSibling.style.display='flex';
            }} 
          />
          <div style={{display: 'none'}} className="w-full h-full flex items-center justify-center">
            <SafeIcon icon={FiImage} className="text-6xl text-white/50" />
          </div>

          {/* Pose ID Overlay */}
          <div className="absolute top-4 left-4">
            <div className="bg-black/70 text-white px-3 py-2 rounded-lg backdrop-blur-sm">
              <span className="font-bold text-sm">{pose.subject} Pose {getPoseNumber(pose.id)}</span>
            </div>
          </div>

          {/* Category Badge */}
          <div className="absolute top-16 left-4">
            <div className="bg-white/90 text-slate-800 px-3 py-2 rounded-lg backdrop-blur-sm">
              <div className="flex items-center">
                <SafeIcon icon={FiCamera} className="mr-2 text-xs" />
                <span className="font-medium text-xs">{pose.subject}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="p-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              {pose.subject} Pose {getPoseNumber(pose.id)}
            </h1>
            <p className="text-sm text-slate-600">Professional pose instruction for photographers</p>
          </div>

          {/* Instruction Box */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-slate-800">Direction Script</h2>
              <motion.button 
                whileHover={{scale: 1.05}} 
                whileTap={{scale: 0.95}} 
                onClick={handleCopyInstruction} 
                className={`flex items-center px-4 py-2 rounded-lg transition-all text-sm ${
                  copied 
                    ? 'bg-brand-accent/20 text-brand-accent-dark' 
                    : 'bg-brand-primary/20 text-brand-primary-dark hover:bg-brand-primary/30'
                }`} 
              >
                <SafeIcon icon={copied ? FiCheck : FiCopy} className="mr-2" />
                {copied ? 'Copied!' : 'Copy'}
              </motion.button>
            </div>
            <p className="text-slate-700 leading-relaxed text-base">
              "{pose.instruction}"
            </p>
          </div>

          {/* Tips Section */}
          <div className="bg-brand-secondary/10 rounded-xl p-6">
            <h3 className="text-base font-semibold text-slate-800 mb-3">Photography Tips</h3>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-start">
                <span className="w-2 h-2 bg-brand-secondary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Read the instruction clearly and slowly to your subject
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-brand-secondary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Allow time for your subject to adjust and get comfortable
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-brand-secondary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Take multiple shots to capture the perfect moment
              </li>
              <li className="flex items-start">
                <span className="w-2 h-2 bg-brand-secondary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                Provide positive feedback to keep your subject relaxed
              </li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default PoseDetail;